import BaseCan from "./baseCan"
import User from "../models/user"
import UserDecorator from "../../formability/admin/models/userDecorator"
import ObjectModelProperty from '~/models/objectModelProperties/objectModelProperty'
import {IObjectModelEntry, IObjectModelPermissionContainer} from '~/interfaces/objectModelInterfaces'
import {ObjectModelPermissionCapabilityType} from '~/enums/objectModelPermissionCapabilityType'
import {ObjectModelPermissionType} from '~/enums/objectModelPermissionType'
import {ObjectHelper} from '~/helpers/objectHelper'
import {IPermission} from '~/interfaces/permissionInterfaces'
import ObjectModelAction from "~/models/objectModelActions/objectModelAction";
import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";

export default class ObjectModelPermissionsCan extends BaseCan {
  constructor(user: User | UserDecorator) {
    super(user)

    /**
     * objectModel could be [ObjectModel, PublishedObjectModel]
     * pulling OM in separately to allow for flexibility
     *
     * @param objectModel evaluates permissions based on currentUserPermissionsArray which should include dynamic permissions (e.g. "Record Holder") calculated on backend
     *
     * Evaluation by objectModel is specific to entry but should not be used when multiple entries are in consideration (e.g. when viewing a ListView table)
     *
     * Since property editing is only done on pages where there is only one entry present
     * this method works well (see 'enable' ability for actions below for alternative evaluation using data on the entry itself, useful when multiple entries are present (e.g. ListView table))
     */
    this.iCan(['edit'], ObjectModelProperty, (property, {objectModel}: { objectModel: IObjectModelPermissionContainer }) => {
      return this.hasPermission({
        permissionType: ObjectModelPermissionType.Property,
        capabilityType: ObjectModelPermissionCapabilityType.Edit,
        permittableClientId: property.client_id,
        permissions: objectModel.currentUserPermissionsArray,
      })
    })

    /**
     * objectModel could be [ObjectModel, PublishedObjectModel]
     * pulling OM in separately to allow for flexibility
     *
     * @param entry if provided, evaluates permissions based on available_action_client_ids specific to the entry
     * @param objectModel evaluates permissions based on currentUserPermissionsArray which should include dynamic permissions (e.g. "Record Holder") calculated on backend
     *
     * Evaluation by entry is specific to entry even when multiple entries are present/in context.
     * Evaluation by objectModel is specific to entry but should not be used when multiple entries are in consideration (e.g. when viewing a ListView table)
     *
     * Use "entry" version whenever possible
     */
    this.iCan(['enable'], ObjectModelAction, (action, {objectModel, entry, contentView}: { objectModel?: IObjectModelPermissionContainer, entry?: IObjectModelEntry, contentView: any }) => {
      if (entry) {
        return action.isAvailableForEntry(entry, user, contentView)
      } else {
        return this.hasPermission({
          permissionType: ObjectModelPermissionType.Action,
          capabilityType: ObjectModelPermissionCapabilityType.Enable,
          permittableClientId: action.client_id,
          permissions: objectModel.currentUserPermissionsArray,
        })
      }
    })

    /**
     * objectModel could be [ObjectModel, PublishedObjectModel]
     * pulling OM in separately to allow for flexibility
     *
     * @param objectModel evaluates permissions based on currentUserPermissionsArray which should include dynamic permissions (e.g. "Record Holder") calculated on backend
     *
     * Evaluation by objectModel is specific to entry but should not be used when multiple entries are in consideration (e.g. when viewing a ListView table)
     *
     * Since relationship editing is only done on pages where there is only one entry present
     * this method works well (see 'enable' ability for actions above for alternative evaluation using data on the entry itself, useful when multiple entries are present (e.g. ListView table))
     */
    this.iCan(['connectOrSever'], ObjectModelRelation, (relation, {objectModel}: { objectModel: IObjectModelPermissionContainer }) => {
      return this.hasPermission({
        permissionType: ObjectModelPermissionType.Relationship,
        capabilityType: ObjectModelPermissionCapabilityType.ConnectOrSever,
        permittableClientId: relation.client_id,
        permissions: objectModel.currentUserPermissionsArray,
      })
    })
  }

  hasPermission({
                  permissionType,
                  capabilityType,
                  permittableClientId,
                  permissions
                }: {
    permissionType: ObjectModelPermissionType,
    capabilityType: ObjectModelPermissionCapabilityType,
    permittableClientId: string,
    permissions: IPermission[]
  }): boolean {
    return ObjectHelper.isAnyMatch<IPermission>(
      {
        permission_type: permissionType,
        capability_type: capabilityType,
        permittable_client_id: permittableClientId,
      },
      ...permissions,
    )
  }
}